import {
  OrderDetailSizeGridRowName,
  SizeGridRowConfig,
  SIZE_GRID_ROWS_CONFIG_DEFAULT,
} from '@epicuro-next/data/order-book';
import {
  IOrderItemWithProductInfo,
  IOrderProduct,
  IOrderProductSize,
  IOrderProductSizeCell,
  SortedScaleSet,
  SortedScaleSetMap,
} from '@epicuro-next/models/order-book';
import * as getValue from 'get-value';
import {
  OrderItem,
  VariantItem,
  VariantSize,
} from 'graphql-api/baskets-api-interfaces';

export const orderDetailSizeGridRowsLocale: {
  [key in OrderDetailSizeGridRowName]: string;
} = {
  [OrderDetailSizeGridRowName.RequestedQuantity]: $localize`:OrderBook|Order Book Detail Size grid Row Requested@@orderDetailSizeGridRowRequested:Requested`,
  [OrderDetailSizeGridRowName.ConfirmedQuantity]: $localize`:OrderBook|Order Book Detail Size grid Row Confirmed@@orderDetailSizeGridRowConfirmed:Confirmed`,
  [OrderDetailSizeGridRowName.UnconfirmedQuantity]: $localize`:OrderBook|Order Book Detail Size grid Row Unconfirmed@@orderDetailSizeGridRowUnconfirmed:Unconfirmed`,
  [OrderDetailSizeGridRowName.DeliveredQuantity]: $localize`:OrderBook|Order Book Detail Size grid Row Delivered@@orderDetailSizeGridRowDelivered:Delivered`,
  [OrderDetailSizeGridRowName.UnitPriceNet]: $localize`:OrderBook|Order Book Detail Size grid Row UnitPriceNet@@orderDetailSizeGridRowUnitPriceNet:Net Unit Price`,
  [OrderDetailSizeGridRowName.Count]: $localize`:OrderBook|Order Book Detail Size grid Row Count@@orderDetailSizeGridRowCount:Count`,
};

interface SizeMap {
  [size: string]: IOrderProductSizeCell;
}
interface SizePropertyMap {
  [propertyName: string]: SizeMap;
}

interface AuxSizeGridMap {
  [auxSize: string]: SizePropertyMap;
}

type AuxSizeColumn = Pick<IOrderProductSizeCell, 'value'>;

export function mapItemsWithProducts(
  items: OrderItem[],
  products: IOrderProduct[],
  sizeGridRows: SizeGridRowConfig[] = SIZE_GRID_ROWS_CONFIG_DEFAULT,
  selectedScaleSet?: string,
): IOrderItemWithProductInfo[] {
  return items.map((item): IOrderItemWithProductInfo => {
    const itemProduct = products.find((product) => product.id === item.id);
    return {
      ...item,
      imageUrl: itemProduct?.thumbnail,
      variants: item.variants?.map((variant) => {
        const result = {
          ...variant,
          sizes: productSizesToVariantSizes(
            itemProduct?.variantSizes || [],
            itemProduct?.variantAux || [],
            itemProduct?.sortedScaleSets || [],
            sizeGridRows,
            variant as VariantItem,
            selectedScaleSet,
            itemProduct?.variantIdentifiedAux ?? [],
          ),
        };

        // TODO: check types conversion
        return result as unknown as IOrderItemWithProductInfo;
      }),
      masterProductId: itemProduct?.masterProductId,
      color:
        itemProduct?.attributes?.color?.[0]?.value ??
        itemProduct?.attributes?.colorText?.[0]?.value ??
        itemProduct?.attributes?.colortext?.[0]?.value ??
        '',
      colorCode:
        itemProduct?.attributes?.colorcode?.[0]?.value ??
        itemProduct?.attributes?.colornumber?.[0]?.value ??
        '',
      colorDescription:
        itemProduct?.attributes?.colordescription?.[0]?.value ?? '',
      colorFamily: itemProduct?.attributes?.colorfamily?.[0]?.value ?? '',
      colorText:
        itemProduct?.attributes?.colorText?.[0]?.value ??
        itemProduct?.attributes?.colortext?.[0]?.value ??
        '',
      division: itemProduct?.attributes?.divisionb2b?.[0]?.value ?? '',
      productClass: itemProduct?.attributes?.productclassb2b?.[0]?.value ?? '',
      title: item.title || itemProduct?.description,
      // category attributes
      targetgrouptext:
        itemProduct?.attributes?.targetgrouptext?.[0]?.value ?? '',
      productclassb2b:
        itemProduct?.attributes?.productclassb2b?.[0]?.value ?? '',
      // end category attributes
    };
  });
}

// tslint:disable-next-line:cognitive-complexity
function productSizesToVariantSizes(
  productSizes: IOrderProductSize[],
  auxSizes: IOrderProductSize[],
  sortedScaleSets: SortedScaleSet[],
  sizeRows: SizeGridRowConfig[],
  variant?: VariantItem,
  selectedScaleSet?: string,
  variantAuxIds?: IOrderProductSize[],
): IOrderProductSizeCell[][] {
  const blankCell = { value: ' ', masterValue: ' ' } as IOrderProductSizeCell;
  const emptySize = { value: '', masterValue: '' } as IOrderProductSizeCell;
  const sizeScaleSetMap = getScaleSetMap(sortedScaleSets, 'size');
  const widthScaleSetMap = getScaleSetMap(sortedScaleSets, 'width');
  /**
   * Build a key-value collection
   */
  const matrix: AuxSizeGridMap = [
    blankCell,
    ...(auxSizes.length ? auxSizes : [emptySize]),
  ]?.reduce((matrixAcc, auxSize: IOrderProductSize) => {
    return {
      ...matrixAcc,
      [auxSize.masterValue]: getSizePerLineCells(
        auxSize,
        productSizes,
        sizeRows,
      ),
    };
  }, {} as AuxSizeGridMap);

  const matrixCells = Object.entries(matrix)
    .filter(([key]) => key !== blankCell.masterValue)
    .map(([_, value]) => value)
    .flatMap(Object.values)
    .flatMap(Object.values) as IOrderProductSizeCell[];

  /**
   * Fill the collection
   */
  variant?.sizes?.forEach((size: VariantSize) => {
    sizeRows.forEach((row) => {
      // Birkenstock has sizes with width without auxSizes, so this size is one dimensional
      const widthIndex = auxSizes.length
        ? (size?.width as string)
        : emptySize.masterValue;
      const sizeMap = (matrix[widthIndex] ?? {})[row.rowId] ?? {};
      const cell =
        sizeMap[size?.title || ''] ||
        matrixCells.find(({ id }) => size?.title?.split(/\W/).includes(id));

      if (cell) {
        cell.value = String(getValue(size, row.pathToSizeProperty) ?? 0);
        cell.unconfirmedQuantity = size.unconfirmedQuantity as number;
      }
    });
  });

  /**
   * Transform the collection into array matrix
   */
  return Object.entries(matrix)
    .sort(([a]) => (a === blankCell.value ? -1 : 0))
    .reduce((acc, [lineTitle, lineValues], i) => {
      let auxSizeColumn: AuxSizeColumn;
      let sortedColumns;
      const showLineTitle = auxSizes.length || sizeRows.length > 1;

      if (i === 0) {
        auxSizeColumn = { value: lineTitle };
        const sortedTitleColumns = getSortedTitleLine(
          Object.values(lineValues)[0],
          sizeScaleSetMap,
        );

        sortedColumns = getTitleColumns(
          sortedTitleColumns,
          sizeScaleSetMap,
          selectedScaleSet,
        );

        return [
          ...acc,
          [...(showLineTitle ? [auxSizeColumn] : []), ...sortedColumns],
        ];
      }

      auxSizeColumn = {
        value: getTitleColumns(
          [lineTitle],
          widthScaleSetMap,
          selectedScaleSet,
        )[0].value,
      };

      if (variantAuxIds?.length) {
        auxSizeColumn = extendWithAuxId(auxSizeColumn, variantAuxIds);
      }

      sortedColumns = Object.entries(lineValues).map(([key, values]) => {
        const rowTitle =
          orderDetailSizeGridRowsLocale[key as OrderDetailSizeGridRowName];
        const auxTitle =
          sizeRows.length > 1
            ? {
                value: auxSizes.length
                  ? `${rowTitle} (${auxSizeColumn?.value})`
                  : rowTitle,
              }
            : {
                ...auxSizeColumn,
              };
        const columns = getSortedLineValues(values, sizeScaleSetMap);
        return [...(showLineTitle ? [auxTitle] : []), ...columns];
      });

      return [...acc, ...sortedColumns];
    }, [] as IOrderProductSizeCell[][]);
}

export function getTitleColumns(
  masterValues: string[],
  sortedScaleSetsMap: SortedScaleSetMap,
  selectedScaleSet?: string,
) {
  const scaleSets = Object.values(sortedScaleSetsMap);
  if (scaleSets.length === 0) {
    return masterValues.map((value) => ({ value }));
  }
  const defaultScaleSet = scaleSets[0]?.defaultValue;

  const isSelectedSetPresentInProductScaleSets =
    selectedScaleSet &&
    // check all product scale sets as selected set can be missed in one of size set
    scaleSets
      .flatMap(({ values }) => values)
      .some(({ key }) => key === selectedScaleSet);

  const usedScaleSet = isSelectedSetPresentInProductScaleSets
    ? selectedScaleSet
    : defaultScaleSet;

  return masterValues.map((masterValue) => ({
    value:
      (sortedScaleSetsMap[masterValue]?.values || []).find(
        ({ key }) => key === usedScaleSet,
      )?.value ?? '-',
  }));
}

export function getScaleSetMap(
  sortedScaleSets: SortedScaleSet[],
  setKey: 'size' | 'width' = 'size',
): SortedScaleSetMap {
  const index = setKey === 'size' ? 0 : 1;
  return (
    sortedScaleSets[index]?.sortedValues.reduce((acc, sortedValue) => {
      const masterValue = sortedValue.values.find(
        ({ key }) => key === sortedValue.defaultValue,
      )?.value;
      return {
        ...acc,
        [masterValue as string]: sortedValue,
      };
    }, {} as SortedScaleSetMap) ?? {}
  );
}

export function getSortedTitleLine(
  lineValues: object,
  sizeScaleSetMap: SortedScaleSetMap,
) {
  return Object.keys(lineValues).sort(
    (a, b) =>
      (sizeScaleSetMap[a]?.sortValue ?? 0) -
      (sizeScaleSetMap[b]?.sortValue ?? 0),
  );
}
export function getSortedLineValues(
  lineValues: object,
  sizeScaleSetMap: SortedScaleSetMap,
) {
  return Object.entries(lineValues)
    .sort(
      ([keyA], [keyB]) =>
        (sizeScaleSetMap[keyA]?.sortValue ?? 0) -
        (sizeScaleSetMap[keyB]?.sortValue ?? 0),
    )
    .map(([_, value]) => value);
}

function getSizePerLineCells(
  auxSize: IOrderProductSize,
  productSizes: IOrderProductSize[],
  childRows: SizeGridRowConfig[],
): SizePropertyMap {
  return childRows.reduce((acc, row: SizeGridRowConfig): SizePropertyMap => {
    return {
      ...acc,
      [row.rowId]: productSizes.reduce(
        (lineAcc, size) => ({
          ...lineAcc,
          [size.masterValue]: {
            id: `${size.masterValue}${auxSize.masterValue}`.trim(),
            value: '',
          } as IOrderProductSizeCell,
        }),
        {},
      ),
    };
  }, {} as SizePropertyMap);
}

export function extendWithAuxId(
  column: AuxSizeColumn,
  variantAuxIds: IOrderProductSize[],
): AuxSizeColumn {
  const auxId = variantAuxIds.find(
    ({ value }) => value === column.value,
  )?.masterValue;

  return auxId
    ? {
        value: `${column.value} - ${auxId}`,
      }
    : column;
}
